/**
 * TranscriptDisplay Component
 * Displays transcribed/extracted text and metadata from both Speech-to-Text and OCR sources
 * Compatible with Node.js 16.20.2
 */

import React, { useRef, useEffect } from 'react';
import '../styles/TranscriptDisplay.css';

function TranscriptDisplay({ transcript, isLoading, processingSource }) {
  const textBoxRef = useRef(null);

  // Auto-scroll to bottom when new content is added
  useEffect(() => {
    if (textBoxRef.current) {
      textBoxRef.current.scrollTop = textBoxRef.current.scrollHeight;
    }
  }, [transcript]);

  /**
   * Copy transcript to clipboard
   */
  const copyToClipboard = () => {
    if (textBoxRef.current) {
      textBoxRef.current.select();
      document.execCommand('copy');
      alert('Transcript copied to clipboard!');
    }
  };

  /**
   * Download transcript as text file
   */
  const downloadTranscript = () => {
    if (transcript.text) {
      const element = document.createElement('a');
      const file = new Blob([transcript.text], { type: 'text/plain' });
      element.href = URL.createObjectURL(file);
      element.download = `transcript_${new Date().getTime()}.txt`;
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  return (
    <div className="transcript-display">
      <div className="transcript-header">
        <h3>📝 Transcript</h3>
        <div className="transcript-actions">
          {transcript.text && (
            <>
              <button
                className="action-btn copy-btn"
                onClick={copyToClipboard}
                title="Copy to clipboard"
              >
                📋 Copy
              </button>
              <button
                className="action-btn download-btn"
                onClick={downloadTranscript}
                title="Download as text file"
              >
                ⬇️ Download
              </button>
            </>
          )}
        </div>
      </div>

      <div className="transcript-content">
        <textarea
          ref={textBoxRef}
          className="transcript-textarea"
          value={transcript.text || ''}
          readOnly
          placeholder="Your transcribed or extracted text will appear here..."
        />
        {isLoading && (
          <div className="loading-indicator">
            <span className="spinner"></span>
            <p>Processing {processingSource === 'speech-to-text' ? 'audio' : 'document'}...</p>
          </div>
        )}
      </div>

      {/* Partial Success Notification - OCR completed but file access requires subscription upgrade */}
      {transcript.metadata?.partialSuccess && (
        <div className="partial-success-notification">
          <div className="notification-icon">📦</div>
          <div className="notification-content">
            <h4>Sarvam Subscription Required</h4>
            <p>
              Your OCR processing completed successfully! However, accessing the extracted output files requires a
              paid Sarvam subscription tier (currently on free/trial tier).
            </p>
            <div className="notification-actions">
              <a
                href="https://sarvam.ai/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="action-link"
              >
                View Sarvam Plans
              </a>
              <span className="action-divider">•</span>
              <a
                href="mailto:support@sarvam.ai"
                className="action-link"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      )}

      {transcript.metadata && (
        <div className="transcript-metadata">
          <div className="metadata-section">
            <h4>📊 Details</h4>
            <div className="metadata-grid">
              {transcript.metadata.source && (
                <div className="metadata-item">
                  <span className="label">Source:</span>
                  <span className="value">{transcript.metadata.source}</span>
                </div>
              )}

              {/* Speech-to-Text specific fields */}
              {transcript.metadata.source === 'Speech-to-Text' && (
                <>
                  {transcript.metadata.request_id && (
                    <div className="metadata-item">
                      <span className="label">Request ID:</span>
                      <span className="value">{transcript.metadata.request_id}</span>
                    </div>
                  )}

                  {transcript.metadata.language_code && (
                    <div className="metadata-item">
                      <span className="label">Language:</span>
                      <span className="value">{transcript.metadata.language_code}</span>
                    </div>
                  )}

                  {transcript.metadata.language_probability && (
                    <div className="metadata-item">
                      <span className="label">Confidence:</span>
                      <span className="value">
                        {(transcript.metadata.language_probability * 100).toFixed(2)}%
                      </span>
                    </div>
                  )}

                  {transcript.metadata.duration_seconds && (
                    <div className="metadata-item">
                      <span className="label">Duration:</span>
                      <span className="value">
                        {transcript.metadata.duration_seconds}s
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* OCR specific fields */}
              {transcript.metadata.source === 'OCR' && (
                <>
                  {transcript.metadata.fileName && (
                    <div className="metadata-item">
                      <span className="label">File Name:</span>
                      <span className="value">{transcript.metadata.fileName}</span>
                    </div>
                  )}

                  {transcript.metadata.fileSize && (
                    <div className="metadata-item">
                      <span className="label">File Size:</span>
                      <span className="value">
                        {(transcript.metadata.fileSize / 1024).toFixed(2)} KB
                      </span>
                    </div>
                  )}

                  {transcript.metadata.tablesExtracted !== undefined && (
                    <div className="metadata-item">
                      <span className="label">Tables Found:</span>
                      <span className="value">{transcript.metadata.tablesExtracted}</span>
                    </div>
                  )}

                  {transcript.metadata.jobId && (
                    <div className="metadata-item">
                      <span className="label">Job ID:</span>
                      <span className="value">{transcript.metadata.jobId}</span>
                    </div>
                  )}

                  {transcript.metadata.processingDetails?.processingTime && (
                    <div className="metadata-item">
                      <span className="label">Processing Time:</span>
                      <span className="value">{transcript.metadata.processingDetails.processingTime}</span>
                    </div>
                  )}

                  {transcript.metadata.processingDetails?.pagesSucceeded !== undefined && transcript.metadata.processingDetails?.totalPages && (
                    <div className="metadata-item">
                      <span className="label">Pages Succeeded / Total:</span>
                      <span className="value">{transcript.metadata.processingDetails.pagesSucceeded}/{transcript.metadata.processingDetails.totalPages}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Speech-to-Text specific sections */}
          {transcript.metadata.source === 'Speech-to-Text' && transcript.metadata.timestamps?.words?.length > 0 && (
            <div className="metadata-section">
              <h4>⏱️ Word Timestamps</h4>
              <div className="timestamps-list">
                {transcript.metadata.timestamps.words.map((word, index) => (
                  <span key={index} className="timestamp-item">
                    {word}{' '}
                    <small>
                      (
                      {transcript.metadata.timestamps.start_time_seconds[index].toFixed(2)}s -{' '}
                      {transcript.metadata.timestamps.end_time_seconds[index].toFixed(2)}s)
                    </small>
                  </span>
                ))}
              </div>
            </div>
          )}

          {transcript.metadata.source === 'Speech-to-Text' && transcript.metadata.diarized_transcript?.entries?.length > 0 && (
            <div className="metadata-section">
              <h4>👥 Speaker Diarization</h4>
              <div className="diarization-list">
                {transcript.metadata.diarized_transcript.entries.map((entry, index) => (
                  <div key={index} className="diarization-item">
                    <div className="speaker-info">
                      <span className="speaker-label">Speaker {entry.speaker_id}</span>
                      <span className="time-range">
                        {entry.start_time_seconds.toFixed(2)}s - {entry.end_time_seconds.toFixed(2)}s
                      </span>
                    </div>
                    <p className="speaker-text">{entry.transcript}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TranscriptDisplay;